/**
 * The playbooks API (vNext-api `docs/playbooks.md`): what each playbook is,
 * a version whole — its manifest, its instructions, its documents — its
 * history, and a new version published from the editor.
 *
 * The manifest travels in its **stored shape** — snake_case, as the folder's
 * `manifest.yaml` reads — and is never camel-cased: what the editor saves
 * must round-trip through the API's `Manifest.load`, which refuses unknown
 * keys. Everything around it is transformed as usual.
 */

/** A client's workbench mode: the workbench owns its live cases; `off` is
 * today's flow alone. */
export type PlaybookMode = 'off' | 'live';
export const PLAYBOOK_MODES: readonly PlaybookMode[] = ['off', 'live'];

export type PlaybookKind = 'house' | 'client';

/** Who releases a draft as the report: a person, or the workbench itself. */
export type PlaybookRelease = 'analyst' | 'automatic';

// ---------------------------------------------------------------------------
// The manifest (stored shape)
// ---------------------------------------------------------------------------

/** Who the playbook is for, and what routes to it. */
export interface ManifestClient {
  /** The client's Backoffice code. */
  code: string;
  /** Hub team ids. */
  teams: string[];
  /** The report config slugs covered. */
  configs: string[];
}

/** One of the client's own report types: its name, the template the
 * document builder opens, and which configs offer it (none: every one). */
export interface ManifestReportType {
  name: string;
  /** A `.docx` under `templates/`; null for a type not drafted here. */
  template?: string | null;
  configs?: string[];
}

export interface PlaybookManifest {
  key: string;
  name: string;
  kind: PlaybookKind;
  client?: ManifestClient | null;
  mode?: PlaybookMode;
  inherits?: 'house' | null;
  /** The client's own code → the type. */
  report_types?: Record<string, ManifestReportType>;
  /** The case's budget: the threshold on model spend across the case. */
  budget_usd?: number | null;
  /** The threshold on what runs may spend before a person has started the case. */
  budget_before_start_usd?: number | null;
  release?: PlaybookRelease | null;
}

// ---------------------------------------------------------------------------
// The documents
// ---------------------------------------------------------------------------

/** What a document is for, which decides when the agents read it. */
export type PlaybookDocumentKind =
  | 'instructions'
  | 'scope'
  | 'standards'
  | 'format'
  | 'qa'
  | 'example'
  | 'template'
  | 'client_material';

export const PLAYBOOK_DOCUMENT_KINDS: readonly PlaybookDocumentKind[] = [
  'instructions',
  'scope',
  'standards',
  'format',
  'qa',
  'example',
  'template',
  'client_material',
];

/** The report types a document applies to: every one, or those listed. */
export type AppliesTo = 'all' | string[];

/** One document of a version: the author's fields, the file's hash and
 * what the system read, and the file to open. */
export interface PlaybookDocument {
  key: string;
  kind: PlaybookDocumentKind | string;
  appliesTo: AppliesTo;
  description: string;
  path: string;
  sha256: string | null;
  /** About this many tokens. */
  size: number | null;
  outline: Record<string, unknown>[];
  fileId: string | null;
}

// ---------------------------------------------------------------------------
// Around the content (transformed)
// ---------------------------------------------------------------------------

export interface PlaybookVersionSummary {
  number: number;
  status: string;
  changeReason: string | null;
  createdBy: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  createdAt: string | null;
}

/** A playbook as the list shows it: its current version's manifest in brief. */
export interface PlaybookSummary {
  key: string;
  name: string;
  kind: PlaybookKind | string;
  mode: PlaybookMode | null;
  clientCode: string | null;
  teams: string[];
  configs: string[];
  reportTypes: string[];
  budgetUsd: number | null;
  budgetBeforeStartUsd: number | null;
  /** The current version's number; null before the first is published. */
  version: number | null;
  publishedAt: string | null;
  publishedBy: string | null;
  changeReason: string | null;
}

/** A version whole. */
export interface PlaybookVersion {
  key: string;
  number: number;
  status: string;
  manifest: PlaybookManifest;
  instructions: string;
  documents: PlaybookDocument[];
  changeReason: string | null;
  createdBy: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  /** The house version a client version was published against. */
  houseVersion: number | null;
}

/** `GET /playbooks/{key}`: the current version whole, and the history. */
export interface PlaybookDetail {
  summary: PlaybookSummary;
  current: PlaybookVersion | null;
  history: PlaybookVersionSummary[];
}

/** An index entry as the editor sends it back: the routing fields of one
 * document (its file stays as published). */
export interface PlaybookEntry {
  key: string;
  kind: PlaybookDocumentKind | string;
  appliesTo: AppliesTo;
  description: string;
}

/** A new version from the editor: the manifest, the instructions and the
 * index entries over the current version's files, with a reason. */
export interface PlaybookPublish {
  manifest: PlaybookManifest;
  instructions: string;
  entries: PlaybookEntry[];
  reason: string;
}

// ---------------------------------------------------------------------------
// Wire shapes and transforms
// ---------------------------------------------------------------------------

export interface ApiPlaybookVersionSummary {
  number: number;
  status: string;
  change_reason?: string | null;
  created_by?: string | null;
  published_by?: string | null;
  published_at?: string | null;
  created_at?: string | null;
}

export interface ApiPlaybookSummary {
  key: string;
  name: string;
  kind: string;
  mode?: string | null;
  client_code?: string | null;
  teams?: string[];
  configs?: string[];
  report_types?: string[];
  budget_usd?: number | null;
  budget_before_start_usd?: number | null;
  version?: number | null;
  published_at?: string | null;
  published_by?: string | null;
  change_reason?: string | null;
}

export interface ApiPlaybookDocument {
  key: string;
  kind: string;
  applies_to?: string | string[] | null;
  description?: string;
  path: string;
  sha256?: string | null;
  size?: number | null;
  outline?: Record<string, unknown>[];
  file_id?: string | null;
}

export interface ApiPlaybookVersion {
  key: string;
  number: number;
  status: string;
  manifest?: Record<string, unknown>;
  instructions?: string;
  documents?: ApiPlaybookDocument[];
  change_reason?: string | null;
  created_by?: string | null;
  published_by?: string | null;
  published_at?: string | null;
  house_version?: number | null;
}

export interface ApiPlaybookDetail {
  summary: ApiPlaybookSummary;
  current?: ApiPlaybookVersion | null;
  history?: ApiPlaybookVersionSummary[];
}

/** The report types a document applies to, as the API sends them: `"all"`,
 * one code, or a list (`["all"]` is every one). */
export function appliesToOf(raw: string | string[] | null | undefined): AppliesTo {
  if (raw === null || raw === undefined || raw === 'all') {
    return 'all';
  }
  if (typeof raw === 'string') {
    return [raw];
  }
  return raw.length === 1 && raw[0] === 'all' ? 'all' : raw;
}

export function transformToPlaybookVersionSummary(
  api: ApiPlaybookVersionSummary
): PlaybookVersionSummary {
  return {
    number: api.number,
    status: api.status,
    changeReason: api.change_reason ?? null,
    createdBy: api.created_by ?? null,
    publishedBy: api.published_by ?? null,
    publishedAt: api.published_at ?? null,
    createdAt: api.created_at ?? null,
  };
}

export function transformToPlaybookSummary(api: ApiPlaybookSummary): PlaybookSummary {
  return {
    key: api.key,
    name: api.name,
    kind: api.kind,
    mode: api.mode === 'live' || api.mode === 'off' ? api.mode : null,
    clientCode: api.client_code ?? null,
    teams: api.teams ?? [],
    configs: api.configs ?? [],
    reportTypes: api.report_types ?? [],
    budgetUsd: api.budget_usd ?? null,
    budgetBeforeStartUsd: api.budget_before_start_usd ?? null,
    version: api.version ?? null,
    publishedAt: api.published_at ?? null,
    publishedBy: api.published_by ?? null,
    changeReason: api.change_reason ?? null,
  };
}

export function transformToPlaybookDocument(api: ApiPlaybookDocument): PlaybookDocument {
  return {
    key: api.key,
    kind: api.kind,
    appliesTo: appliesToOf(api.applies_to),
    description: api.description ?? '',
    path: api.path,
    sha256: api.sha256 ?? null,
    size: api.size ?? null,
    outline: api.outline ?? [],
    fileId: api.file_id ?? null,
  };
}

export function transformToPlaybookVersion(api: ApiPlaybookVersion): PlaybookVersion {
  return {
    key: api.key,
    number: api.number,
    status: api.status,
    manifest: (api.manifest ?? {}) as unknown as PlaybookManifest,
    instructions: api.instructions ?? '',
    documents: (api.documents ?? []).map(transformToPlaybookDocument),
    changeReason: api.change_reason ?? null,
    createdBy: api.created_by ?? null,
    publishedBy: api.published_by ?? null,
    publishedAt: api.published_at ?? null,
    houseVersion: api.house_version ?? null,
  };
}

export function transformToPlaybookDetail(api: ApiPlaybookDetail): PlaybookDetail {
  return {
    summary: transformToPlaybookSummary(api.summary),
    current: api.current ? transformToPlaybookVersion(api.current) : null,
    history: (api.history ?? []).map(transformToPlaybookVersionSummary),
  };
}

/** A publish as the API takes it. */
export function fromPlaybookPublish(body: PlaybookPublish): Record<string, unknown> {
  return {
    manifest: body.manifest,
    instructions: body.instructions,
    entries: body.entries.map((e) => ({
      key: e.key,
      kind: e.kind,
      applies_to: e.appliesTo,
      description: e.description,
    })),
    reason: body.reason,
  };
}

/** The entries of a version, as the editor starts from them. */
export function entriesOf(version: PlaybookVersion): PlaybookEntry[] {
  return version.documents.map((d) => ({
    key: d.key,
    kind: d.kind,
    appliesTo: d.appliesTo,
    description: d.description,
  }));
}
